<template>
  <v-card outlined class="pa-3 field-editor">
    <div class="d-flex justify-space-between align-center mb-2">
      <div class="d-flex align-center">
        <div class="text-body-2 font-weight-medium">
          {{ getFieldLabel() }}
        </div>
        <v-icon v-if="field.type === 'object'"
          small
          :class="{'ml-2': true, 'rotate-90': isExpanded}"
          @click="toggleExpanded"
          color="grey"
        >
          mdi-chevron-right
        </v-icon>
      </div>
      <v-btn
        icon
        x-small
        color="error"
        @click="handleRemoveField"
      >
        <v-icon x-small>mdi-delete</v-icon>
      </v-btn>
    </div>

    <v-row dense>
      <v-col cols="12" md="4">
        <v-text-field
          outlined
          dense
          label="Field Name"
          placeholder="e.g., invoiceNumber, address"
          :value="fieldName"
          @input="handleUpdateFieldName"
          class="mb-2"
        />
      </v-col>
      <v-col cols="12" md="4">
        <v-select
          outlined
          dense
          label="Field Type"
          :value="field.type"
          @input="handleUpdateFieldType"
          :items="fieldTypes"
          class="mb-2"
        />
      </v-col>
      <v-col cols="12" md="4">
        <v-checkbox
          label="Required Field"
          :input-value="isRequired"
          @change="handleToggleRequired"
          class="mt-0 pt-0"
        />
      </v-col>
    </v-row>

    <v-textarea
      outlined
      dense
      label="Field Description"
      placeholder="Describe what this field represents and how to identify it in documents"
      :value="field.description"
      @input="(val) => handleUpdateField('description', val)"
      rows="2"
    />

    <v-select
      v-if="field.type === 'array'"
      outlined
      dense
      label="Array Items Type"
      :value="field.items?.type || 'string'"
      @input="handleUpdateArrayItemType"
      :items="fieldTypes.filter(t => t.value !== 'array')"
      class="mt-2"
    />

    <div v-if="field.type === 'object'" class="mt-3">
      <v-expand-transition>
        <div v-show="isExpanded">
          <v-divider class="mb-3"></v-divider>

          <div class="d-flex align-center mb-3">
            <div class="text-body-2 font-weight-medium">Nested Fields:</div>
            <v-spacer></v-spacer>
            <v-btn
              x-small
              outlined
              color="primary"
              @click="addNestedField"
            >
              <v-icon x-small left>mdi-plus</v-icon>
              Add Field
            </v-btn>
          </div>

          <div v-if="field.properties && Object.keys(field.properties).length > 0"
               class="nested-fields pl-4">
            <div v-for="(nestedField, nestedFieldName, nestedIndex) in field.properties"
                 :key="`${parentPath}-${fieldName}-nested-${nestedIndex}`"
                 class="mb-3">
              <extract-field-editor
                :field-name="nestedFieldName"
                :field="nestedField"
                :index="nestedIndex"
                :is-required="isNestedFieldRequired(nestedFieldName)"
                :parent-path="getNestedPath()"
                :depth="depth + 1"
                @update-field="handleNestedUpdateField"
                @update-field-name="handleNestedUpdateFieldName"
                @remove-field="handleNestedRemoveField"
                @toggle-required="handleNestedToggleRequired"
              />
            </div>
          </div>

          <div v-else class="text-center py-3 grey--text">
            <v-icon color="grey lighten-1" class="mb-1">mdi-folder-open-outline</v-icon>
            <div class="text-caption">No nested fields defined</div>
            <div class="text-caption">Click "Add Field" to create nested properties</div>
          </div>
        </div>
      </v-expand-transition>
    </div>
  </v-card>
</template>

<script>
export default {
  name: 'ExtractFieldEditor',
  props: {
    fieldName: {
      type: String,
      required: true
    },
    field: {
      type: Object,
      required: true
    },
    index: {
      type: Number,
      required: true
    },
    isRequired: {
      type: Boolean,
      default: false
    },
    parentPath: {
      type: String,
      default: ''
    },
    depth: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      isExpanded: false,
      fieldTypes: [
        { text: 'String (text)', value: 'string' },
        { text: 'Number', value: 'number' },
        { text: 'Integer', value: 'integer' },
        { text: 'Boolean (true/false)', value: 'boolean' },
        { text: 'Array (list)', value: 'array' },
        { text: 'Object (nested)', value: 'object' }
      ]
    };
  },
  methods: {
    getFieldLabel() {
      const depthPrefix = '  '.repeat(this.depth);
      return `${depthPrefix}Field ${this.index + 1}${this.fieldName ? ': ' + this.fieldName : ''}`;
    },
    getNestedPath() {
      return this.parentPath ? `${this.parentPath}.${this.fieldName}` : this.fieldName;
    },
    toggleExpanded() {
      this.isExpanded = !this.isExpanded;
    },
    handleUpdateFieldName(newName) {
      this.$emit('update-field-name', this.fieldName, newName, this.parentPath);
    },
    handleUpdateField(property, value) {
      this.$emit('update-field', this.fieldName, property, value, this.parentPath);
    },
    handleUpdateFieldType(newType) {
      if (newType === 'object' && !this.field.properties) {
        const updatedField = {
          ...this.field,
          type: newType,
          properties: {},
          required: []
        };
        this.$emit('update-field', this.fieldName, null, updatedField, this.parentPath, true);
      } else {
        this.handleUpdateField('type', newType);
      }
    },
    handleUpdateArrayItemType(itemType) {
      this.handleUpdateField('items', { type: itemType });
    },
    handleToggleRequired(isRequired) {
      this.$emit('toggle-required', this.fieldName, isRequired, this.parentPath);
    },
    handleRemoveField() {
      this.$emit('remove-field', this.fieldName, this.parentPath);
    },
    addNestedField() {
      const nestedFieldName = `field${Object.keys(this.field.properties || {}).length + 1}`;
      const newField = {
        type: 'string',
        description: ''
      };

      this.$emit('update-field', this.fieldName, 'properties', {
        ...(this.field.properties || {}),
        [nestedFieldName]: newField
      }, this.parentPath);

      this.isExpanded = true;
    },
    handleNestedUpdateField(nestedFieldName, property, value, nestedParentPath, isFullReplace = false) {
      const nestedPath = this.getNestedPath();
      this.$emit('update-field', nestedFieldName, property, value, nestedPath, isFullReplace);
    },
    handleNestedUpdateFieldName(oldName, newName) {
      const nestedPath = this.getNestedPath();
      this.$emit('update-field-name', oldName, newName, nestedPath);
    },
    handleNestedRemoveField(nestedFieldName) {
      const nestedPath = this.getNestedPath();
      this.$emit('remove-field', nestedFieldName, nestedPath);
    },
    handleNestedToggleRequired(nestedFieldName, isRequired) {
      const nestedPath = this.getNestedPath();
      this.$emit('toggle-required', nestedFieldName, isRequired, nestedPath);
    },
    isNestedFieldRequired(nestedFieldName) {
      return this.field.required?.includes(nestedFieldName) || false;
    }
  }
};
</script>

<style scoped lang="scss">
.field-editor {
  position: relative;
}

.nested-fields {
  border-left: 2px solid #e0e0e0;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: -6px;
    top: 0;
    bottom: 0;
    width: 2px;
    background: linear-gradient(to bottom, #2196F3 0%, #2196F3 100%);
  }
}

.rotate-90 {
  transform: rotate(90deg);
  transition: transform 0.2s ease;
}

.v-icon {
  transition: transform 0.2s ease;
}
</style>